import { html, unsafeCSS } from 'lit';
import { state } from 'lit/decorators.js';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafBreadcrumbs } from '@cigna/dcws_leaf-web-components/components/breadcrumbs/breadcrumbs.js';
import { LeafBreadcrumbsItem } from '@cigna/dcws_leaf-web-components/components/breadcrumbs-item/breadcrumbs-item.js';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafAccordion } from '@cigna/dcws_leaf-web-components/components/accordion/accordion.js';
import { CHCHero } from '@cigna/dcws_leaf-web-components/chc-components/hero/hero.component.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import { CHCRelatedLinks } from '../related-links/related-links.js';
import styles from './tag-page.scss?inline';
import type { TagPageLocale } from './interfaces.js';

export class CHCTagPage extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  static override styles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-breadcrumbs': LeafBreadcrumbs,
    'leaf-breadcrumbs-item': LeafBreadcrumbsItem,
    'leaf-accordion': LeafAccordion,
    'chc-hero': CHCHero,
    'chcapp-related-links': CHCRelatedLinks,
  };
  protected override ns = 'tag-page';
  private tagPageLocale?: TagPageLocale;

  @state()
  totalLinks = 0;

  get articleTag() {
    return new URLSearchParams(window.location.search).get('tag') || '';
  }

  get articleTagClean() {
    // @ts-ignore next-line
    return this.articleTag.replaceAll('_', ' ');
  }

  get articleTagLowerCase() {
    return this.articleTag.toLowerCase();
  }

  getLocaleURL(enURL: string) {
    return this.i18n?.language === 'es-us' ? `/es-us${enURL}` : enURL;
  }

  redirect() {
    const redirect = this.getLocaleURL('/knowledge-center/');
    window.location.assign(redirect);
  }

  override onNamespacesLoaded = () => {
    this.tagPageLocale = this.i18n?.getLanguageData(this.ns) as TagPageLocale;
    if (!this.articleTag) {
      this.redirect();
    }
  };

  protected override async firstUpdated() {
    document.title = `${this.articleTagClean} | Cigna Healthcare`;
    await this.updateComplete;
    window.addEventListener('chcapp-related-links:service', (event) => {
      if ((event as CustomEvent).detail.error) {
        this.redirect();
      }
      this.totalLinks = (event as CustomEvent).detail.relatedLinks.length;
    });
  }

  override render() {
    if (!this.i18n || this.i18n.loading || !this.articleTag) {
      return html`<slot name="loading"></slot>`;
    }
    if (this.i18n?.error) {
      return html`<slot name="error"></slot>`;
    }

    return html`
      <div class="large-7">
        <div class="leaf-grid-container">
          <leaf-breadcrumbs>
            <leaf-breadcrumbs-item href="${this.getLocaleURL('/')}"
              >${this.tagPageLocale?.breadcrumbHome}</leaf-breadcrumbs-item
            >
            <leaf-breadcrumbs-item href="${this.getLocaleURL('/knowledge-center/')}" mobile-breadcrumb="true">
              ${this.tagPageLocale?.breadcrumbKnowledgeCenter}
            </leaf-breadcrumbs-item>
            <leaf-breadcrumbs-item>${this.articleTagClean}</leaf-breadcrumbs-item>
          </leaf-breadcrumbs>
        </div>
      </div>
      <chc-hero variant="tertiary" background="navy" pattern="leaf-circle">
        <div slot="meta"><p>${this.tagPageLocale?.tag}:</p></div>
        <h1 slot="heading">${this.articleTagClean}</h1>
      </chc-hero>
      <chcapp-related-links
        relateby="section:knowledge_center|tags:${this.articleTagLowerCase}"
        domain="www-cigna-com"
        limit="4"
        class="leaf-grid-container large-7"
      >
        <h3 slot="heading">${this.totalLinks} ${this.tagPageLocale?.articlesFor} "${this.articleTagClean}"</h3>
        <slot name="error-related-links"></slot>
        <slot name="loading-related-links"></slot>
      </chcapp-related-links>
    `;
  }
}
